// Renders the audit JSON returned by /public/audit/pdp.
// Visual style is a faithful port of /Users/dev/Downloads/pdp-audit-report (1).jsx —
// white cards on a light-gray page, gold (#b8966a) accents, Oswald display +
// Lato body. The reference's inner white header bar (logo + "Audit Another
// Page" button) is intentionally NOT included; the dark photo-collage header
// from PublicShareContainer sits above this on the public page.

import React, { useEffect, useRef, useState } from "react";
import { pdf } from "@react-pdf/renderer";
import {
    AGENTIC_COMMERCE_DIMENSION_LABELS,
    HUMAN_CONVERSION_SECTION_LABELS,
    PdpAudit,
    PdpAuditResponse
} from "./types";
import { parseScore, projectAgenticCommerce, projectHumanConversion, projectOverall } from "./projection";
import { PdpAuditPdf } from "./PdpAuditPdf";
import axiosInstant from "../../api";
import { auditPdpReportEmailEndpoint } from "../../api/endpoints";
import { buildRegisterHandoffPath, buildStrategyPath } from "../../utils/funnelHandoff";
import FunnelChoiceCard from "../FunnelCta/FunnelChoiceCard";

// DOM/SVG accent — brand primary under RMN theming, TDC report gold otherwise.
// (The PDF renderer keeps its own literal hexes in PdpAuditPdf.tsx.)
const GOLD = "var(--rmn-primary, #b8966a)";
// Neutral roles: the literal is the light/public value; --tool-* exists only
// in the portal's dark mode (src/theme/tokens.ts) and maps onto real tokens.
const BLACK = "var(--tool-text, #1a1a1a)";
// Text-on-color (gold CTAs, the dark hero) — white in both themes.
const WHITE = "#ffffff";
// Card plate.
const CARD = "var(--tool-card, #ffffff)";
const GRAY = "var(--tool-text-2, #666666)";
const GRAY_LIGHT = "var(--tool-muted, #999999)";
const BORDER = "var(--tool-border, #e5e5e5)";
// Page background defaults to white so the embed-host's `?bg=` color (passed
// in via `bgColor`) wins. No hardcoded gray. (Dark portal: the page token.)
const BG_DEFAULT = "var(--tool-page, #ffffff)";

const DISPLAY = "'Oswald', sans-serif";
const BODY = "'Lato', sans-serif";

interface RadarDatum {
    label: string;
    value: number;
    max: number;
}

function wrapLabel(label: string, max: number): string[] {
    if (label.length <= max) return [label];
    const words = label.split(/\s+/);
    const lines: string[] = [];
    let buf = "";
    for (const w of words) {
        if ((buf + " " + w).trim().length > max) {
            if (buf) lines.push(buf);
            buf = w;
        } else {
            buf = (buf + " " + w).trim();
        }
    }
    if (buf) lines.push(buf);
    return lines;
}

const RadarChart: React.FC<{ data: RadarDatum[]; size?: number }> = ({ data, size = 320 }) => {
    const cx = size / 2;
    const cy = size / 2;
    const R = size * 0.32;
    const n = data.length;
    const step = (2 * Math.PI) / n;
    const off = -Math.PI / 2;
    const pt = (i: number, v: number, mx: number = 100) => {
        const a = off + i * step;
        const r = (v / mx) * R;
        return [cx + r * Math.cos(a), cy + r * Math.sin(a)] as const;
    };
    const rings = [0.2, 0.4, 0.6, 0.8, 1.0];
    return (
        <svg
            viewBox={`0 0 ${size} ${size}`}
            style={{
                width: "100%",
                maxWidth: size,
                display: "block",
                margin: "0 auto"
            }}
        >
            {rings.map((p, ri) => (
                <polygon
                    key={ri}
                    points={data.map((_, i) => pt(i, p * 100).join(",")).join(" ")}
                    fill="none"
                    stroke={BORDER}
                    strokeWidth="0.5"
                    strokeDasharray={ri < 4 ? "2,2" : "none"}
                />
            ))}
            {data.map((_, i) => {
                const [ex, ey] = pt(i, 100);
                return <line key={i} x1={cx} y1={cy} x2={ex} y2={ey} stroke={BORDER} strokeWidth="0.4" />;
            })}
            <polygon
                points={data.map((d, i) => pt(i, d.value, d.max).join(",")).join(" ")}
                fill={GOLD}
                fillOpacity="0.12"
                stroke={GOLD}
                strokeWidth="2"
            />
            {data.map((d, i) => {
                const [px, py] = pt(i, d.value, d.max);
                return <circle key={i} cx={px} cy={py} r="3" fill={GOLD} />;
            })}
            {data.map((d, i) => {
                // Numeric value just outside each data point along the
                // radial direction so it stays clear of the polygon fill.
                const a = off + i * step;
                const r = (d.value / d.max) * R + 11;
                const vx = cx + r * Math.cos(a);
                const vy = cy + r * Math.sin(a);
                return (
                    <text
                        key={`v-${i}`}
                        x={vx}
                        y={vy}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fill={BLACK}
                        fontSize="9"
                        fontFamily={BODY}
                        fontWeight="700"
                    >
                        {d.value}
                    </text>
                );
            })}
            {data.map((d, i) => {
                const a = off + i * step;
                const lr = R + 38;
                const lx = cx + lr * Math.cos(a);
                const ly = cy + lr * Math.sin(a);
                const lines = wrapLabel(d.label, 16);
                return (
                    <text
                        key={i}
                        x={lx}
                        y={ly}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fill={GRAY}
                        fontSize="8"
                        fontFamily={BODY}
                        fontWeight="400"
                    >
                        {lines.map((line, li) => (
                            <tspan key={li} x={lx} dy={li === 0 ? -((lines.length - 1) * 5) : 10}>
                                {line}
                            </tspan>
                        ))}
                    </text>
                );
            })}
        </svg>
    );
};

const Card: React.FC<{
    children: React.ReactNode;
    style?: React.CSSProperties;
}> = ({ children, style }) => (
    <div
        style={{
            background: CARD,
            borderRadius: 8,
            border: `1px solid ${BORDER}`,
            padding: "32px 36px",
            marginBottom: 20,
            ...style
        }}
    >
        {children}
    </div>
);

function prettyKey(camel: string): string {
    return camel
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (c) => c.toUpperCase())
        .trim();
}

const Table: React.FC<{ columns: string[]; rows: React.ReactNode[][] }> = ({ columns, rows }) => (
    <div style={{ overflowX: "auto" }}>
        <table
            style={{
                width: "100%",
                borderCollapse: "collapse",
                fontFamily: BODY,
                fontSize: 13.5
            }}
        >
            <thead>
                <tr>
                    {columns.map((c, i) => (
                        <th
                            key={i}
                            style={{
                                padding: "10px 14px",
                                textAlign: "left",
                                fontWeight: 600,
                                color: BLACK,
                                fontSize: 13,
                                borderBottom: `2px solid ${BLACK}`,
                                whiteSpace: "nowrap"
                            }}
                        >
                            {c}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {rows.map((row, ri) => (
                    <tr key={ri}>
                        {row.map((cell, ci) => (
                            <td
                                key={ci}
                                style={{
                                    padding: "14px 14px",
                                    borderBottom: `1px solid ${BORDER}`,
                                    color: ci === 0 ? BLACK : GRAY,
                                    fontWeight: ci === 0 ? 500 : 400,
                                    lineHeight: 1.55,
                                    verticalAlign: "top"
                                }}
                            >
                                {cell}
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

const ScoreDisplay: React.FC<{
    value: number;
    max?: number;
    label: string;
    isPercent?: boolean;
}> = ({ value, max, label, isPercent }) => (
    <div style={{ textAlign: "center", flex: 1 }}>
        <div
            style={{
                fontFamily: DISPLAY,
                fontSize: isPercent ? 44 : 36,
                color: GOLD,
                fontWeight: 500,
                lineHeight: 1
            }}
        >
            {isPercent ? (
                `${value}%`
            ) : (
                <>
                    {value}{" "}
                    <span
                        style={{
                            fontSize: 20,
                            fontWeight: 300,
                            color: GRAY_LIGHT
                        }}
                    >
                        / {max}
                    </span>
                </>
            )}
        </div>
        <div
            style={{
                fontFamily: BODY,
                fontSize: 10,
                letterSpacing: 1.5,
                color: GRAY_LIGHT,
                marginTop: 8,
                fontWeight: 400,
                textTransform: "uppercase"
            }}
        >
            {label}
        </div>
    </div>
);

function sectionToPercent(score: string): number {
    const { current, max } = parseScore(score);
    if (!max) return 0;
    return Math.round((current / max) * 100);
}

interface Props {
    data: PdpAuditResponse;
    onAuditAnother?: () => void;
    remainingToday?: number;
    bgColor?: string;
    isLoggedIn?: boolean;
    auditEmail?: string;
    // Create Proposal CTA target. The RMN portal passes its white-label
    // wizard path (/portal/:brandSlug/proposal); defaults to the public page.
    proposalPath?: string;
    // CTA label — the brand portal says "Create Strategy" (TDC-1499
    // terminology); the public tool keeps "Create Proposal".
    proposalCtaLabel?: string;
    // The RMN portal passes false — its surfaces have no email-report
    // feature. The button also needs auditEmail (the /public relay sends to
    // the submitted address), so it only ever renders on the public tool.
    showEmailAction?: boolean;
    // Product identity for the CTA (TDC-1499): pre-seeds the strategy
    // wizard's product picker via ?desireid= (imported catalog product) or
    // ?dp= (discovered staging row — auto-imported at create), and the TDC
    // category dropdown via ?category=.
    productRef?: {
        desireid?: number;
        discoveredProductId?: number;
        categoryId?: number | null;
        // Viewing a catalog product's score (no audit report row): the
        // wizard's picker reads this instead of ?audit=.
        pdpUrlScoreId?: number | null;
    };
}

// Server-shaped ReportProductRef (types/rmn.ts) → the CTA prop above.
// Lives here (not auditReportShared) so MultiAuditSynthesisReport can import
// it without a circular module edge.
export const toCtaProductRef = (
    ref:
        | {
              desireid?: number;
              discovered_product_id?: number;
              product_category_id?: number | null;
          }
        | undefined
): Props["productRef"] =>
    ref
        ? {
              desireid: ref.desireid,
              discoveredProductId: ref.discovered_product_id,
              categoryId: ref.product_category_id
          }
        : undefined;

type EmailStatus =
    | { kind: "idle" }
    | { kind: "sending" }
    | { kind: "sent" }
    | { kind: "error"; message: string };

function blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(reader.error);
        reader.onload = () => {
            const result = reader.result as string;
            const comma = result.indexOf(",");
            resolve(comma >= 0 ? result.slice(comma + 1) : result);
        };
        reader.readAsDataURL(blob);
    });
}

export function PdpAuditReport({
    data,
    onAuditAnother,
    remainingToday,
    bgColor,
    isLoggedIn,
    auditEmail,
    proposalPath = "/proposal",
    proposalCtaLabel = "Create Proposal",
    showEmailAction = true,
    productRef
}: Props) {
    const { audit, meta } = data;
    const [downloading, setDownloading] = useState(false);
    const [emailStatus, setEmailStatus] = useState<EmailStatus>({ kind: "idle" });

    const fileSlug = (audit.productName || "product")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const blob = await pdf(<PdpAuditPdf data={data} />).toBlob();
            const objectUrl = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = objectUrl;
            a.download = `PDP-Audit-${fileSlug || "product"}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(objectUrl);
        } finally {
            setDownloading(false);
        }
    };

    const handleEmailReport = async () => {
        if (!auditEmail) {
            setEmailStatus({
                kind: "error",
                message: "We couldn't find the email address you submitted. Please run the audit again."
            });
            return;
        }
        setEmailStatus({ kind: "sending" });
        try {
            const blob = await pdf(<PdpAuditPdf data={data} />).toBlob();
            const pdfBase64 = await blobToBase64(blob);
            await axiosInstant.post(auditPdpReportEmailEndpoint, {
                email: auditEmail,
                url: meta.url,
                filename: `PDP-Audit-${fileSlug || "product"}.pdf`,
                pdfBase64
            });
            setEmailStatus({ kind: "sent" });
        } catch (err: any) {
            const code = err?.response?.data?.error;
            const friendly =
                code === "AuditNotFound"
                    ? "We couldn't match this report to your recent audit. Please run the audit again."
                    : code === "PdfTooLarge"
                        ? "This report is too large to email. Please use Download instead."
                        : code === "EmailDomainNotAllowed"
                            ? "Please use a company email address — consumer providers (gmail, yahoo, etc.) aren't supported."
                            : "We couldn't send the email. Please try again in a moment.";
            setEmailStatus({ kind: "error", message: friendly });
        }
    };

    // Anonymous users only see a partial preview on screen; the full report is
    // emailed to them automatically. Fire the send once when the report mounts.
    // The ref guard prevents React 18 StrictMode's double-invoke from sending
    // twice.
    const autoSentRef = useRef(false);
    useEffect(() => {
        if (isLoggedIn || !auditEmail || autoSentRef.current) return;
        autoSentRef.current = true;
        handleEmailReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn, auditEmail]);

    const humanRadar: RadarDatum[] = HUMAN_CONVERSION_SECTION_LABELS.map(({ key, label }) => ({
        label,
        value: sectionToPercent(audit.humanConversion[key].score),
        max: 100
    }));

    const agenticRadar: RadarDatum[] = AGENTIC_COMMERCE_DIMENSION_LABELS.map(({ key, label }) => ({
        label,
        value: sectionToPercent(audit.agenticCommerce[key].score),
        max: 100
    }));

    const humanRows: React.ReactNode[][] = HUMAN_CONVERSION_SECTION_LABELS.map(({ key, label }) => {
        const s = audit.humanConversion[key];
        return [label, s.score, s.strengths, s.gaps];
    });

    const agenticRows: React.ReactNode[][] = AGENTIC_COMMERCE_DIMENSION_LABELS.map(({ key, label }) => {
        const s = audit.agenticCommerce[key];
        return [label, s.score, s.strengths, s.gaps];
    });

    const human = projectHumanConversion(audit);
    const agentic = projectAgenticCommerce(audit);
    const overall = projectOverall(audit);

    const liftStyle: React.CSSProperties = {
        color: "#16a34a",
        fontWeight: 700
    };
    const projectedRows: React.ReactNode[][] = [
        [
            "Human Conversion Readiness",
            String(audit.scores.humanConversion),
            String(human.projectedTotal),
            <span key="h" style={liftStyle}>
                +{Math.max(0, human.projectedTotal - audit.scores.humanConversion)}
            </span>
        ],
        [
            "Agentic Commerce Readiness",
            String(audit.scores.agenticCommerce),
            String(agentic.projectedTotal),
            <span key="a" style={liftStyle}>
                +{Math.max(0, agentic.projectedTotal - audit.scores.agenticCommerce)}
            </span>
        ],
        [
            "Overall PDP Effectiveness",
            `${overall.current}%`,
            `${overall.projected}%`,
            <span key="o" style={liftStyle}>
                +{Math.max(0, overall.projected - overall.current)}%
            </span>
        ]
    ];

    const humanLiftRows: React.ReactNode[][] = human.rows.map((r) => [
        prettyKey(r.label),
        String(r.current),
        String(r.projected),
        r.driver
    ]);
    const agenticLiftRows: React.ReactNode[][] = agentic.rows.map((r) => [
        prettyKey(r.label),
        String(r.current),
        String(r.projected),
        r.driver
    ]);

    // The strategy builder link; anonymous visitors also get the register
    // link (lib/funnelClaim imports this product + seeds its score from
    // this report).
    const strategyHref = buildStrategyPath(proposalPath, {
        title: audit.productName,
        url: meta.url,
        brand: audit.brandName,
        email: auditEmail,
        auditReportId: meta.reportId,
        category: productRef && productRef.categoryId ? productRef.categoryId : null,
        score: productRef && productRef.pdpUrlScoreId ? productRef.pdpUrlScoreId : null
    });
    const strategyLinkHref =
        productRef && (productRef.desireid || productRef.discoveredProductId)
            ? `${strategyHref}${strategyHref.includes("?") ? "&" : "?"}${
                  productRef.desireid
                      ? `desireid=${productRef.desireid}`
                      : `dp=${productRef.discoveredProductId}`
              }`
            : strategyHref;
    const registerHref = buildRegisterHandoffPath({
        email: auditEmail,
        url: meta.url,
        auditReportId: meta.reportId
    });

    return (
        <div style={{ fontFamily: BODY, background: bgColor || BG_DEFAULT, minHeight: "100vh" }}>
            <div
                style={{
                    maxWidth: 720,
                    margin: "0 auto",
                    padding: "32px 20px 60px"
                }}
            >
                <h1
                    style={{
                        fontFamily: DISPLAY,
                        fontSize: 32,
                        fontWeight: 700,
                        color: BLACK,
                        margin: "0 0 8px"
                    }}
                >
                    Product Page Audit
                </h1>
                <p
                    style={{
                        fontFamily: BODY,
                        fontSize: 14,
                        color: GRAY,
                        lineHeight: 1.5,
                        margin: "0 0 28px"
                    }}
                >
                    Drop in a product detail page URL and we'll score it on Human Conversion Readiness and Agentic
                    Commerce Readiness.
                </p>

                {/* ── Overview Card ── */}
                <Card>
                    <div
                        style={{
                            fontFamily: BODY,
                            fontSize: 11,
                            letterSpacing: 1,
                            color: GRAY_LIGHT,
                            textTransform: "uppercase",
                            marginBottom: 4
                        }}
                    >
                        Product Page Audit
                    </div>
                    <h2
                        style={{
                            fontFamily: DISPLAY,
                            fontSize: 28,
                            fontWeight: 700,
                            color: BLACK,
                            margin: "0 0 2px",
                            textTransform: "uppercase"
                        }}
                    >
                        {audit.brandName || "Unknown Brand"}
                    </h2>
                    <div
                        style={{
                            fontFamily: BODY,
                            fontSize: 15,
                            color: GRAY,
                            fontStyle: "italic",
                            marginBottom: 2
                        }}
                    >
                        {audit.productName || "Unknown Product"}
                    </div>
                    <div
                        style={{
                            fontFamily: BODY,
                            fontSize: 12,
                            color: GRAY_LIGHT,
                            marginBottom: 20,
                            wordBreak: "break-all"
                        }}
                    >
                        {meta.url}
                    </div>

                    <div
                        style={{
                            display: "inline-block",
                            background: GOLD,
                            padding: "8px 24px",
                            borderRadius: 4,
                            marginBottom: 28
                        }}
                    >
                        <span
                            style={{
                                fontFamily: DISPLAY,
                                fontSize: 14,
                                letterSpacing: 2,
                                color: WHITE,
                                fontWeight: 500,
                                textTransform: "uppercase"
                            }}
                        >
                            Overall Score: {audit.scores.overall}%
                        </span>
                    </div>

                    <div
                        style={{
                            display: "flex",
                            gap: 16,
                            paddingTop: 20,
                            borderTop: `1px solid ${BORDER}`
                        }}
                    >
                        <ScoreDisplay
                            value={audit.scores.humanConversion}
                            max={100}
                            label="Human Conversion Readiness"
                        />
                        <ScoreDisplay
                            value={audit.scores.agenticCommerce}
                            max={100}
                            label="Agentic Commerce Readiness"
                        />
                        <ScoreDisplay value={audit.scores.overall} label="Overall PDP Effectiveness" isPercent />
                    </div>
                </Card>

                {!isLoggedIn && (
                    <FunnelChoiceCard
                        testId="funnel-choice-top"
                        registerHref={registerHref}
                        strategyHref={strategyLinkHref}
                    />
                )}

                {/* ── Key Findings Card ── */}
                <Card>
                    <h2
                        style={{
                            fontFamily: DISPLAY,
                            fontSize: 22,
                            fontWeight: 600,
                            color: BLACK,
                            margin: "0 0 20px"
                        }}
                    >
                        Key Findings
                    </h2>
                    {/* Anonymous users get a teaser: the findings fade out toward
                        the bottom, with the full breakdown delivered by email. */}
                    <div
                        style={{
                            display: "flex",
                            gap: 32,
                            ...(!isLoggedIn && {
                                position: "relative",
                                maxHeight: 300,
                                overflow: "hidden",
                                WebkitMaskImage:
                                    "linear-gradient(to bottom, #000 30%, transparent 100%)",
                                maskImage:
                                    "linear-gradient(to bottom, #000 30%, transparent 100%)"
                            })
                        }}
                    >
                        <div style={{ flex: 1 }}>
                            <h3
                                style={{
                                    fontFamily: BODY,
                                    fontSize: 13,
                                    fontWeight: 700,
                                    color: BLACK,
                                    textTransform: "uppercase",
                                    letterSpacing: 0.5,
                                    margin: "0 0 12px",
                                    textDecoration: "underline",
                                    textUnderlineOffset: 4
                                }}
                            >
                                Strengths
                            </h3>
                            {audit.keyFindings.strengths.map((t, i) => (
                                <p
                                    key={i}
                                    style={{
                                        fontFamily: BODY,
                                        fontSize: 13.5,
                                        color: GRAY,
                                        lineHeight: 1.65,
                                        margin: "0 0 10px"
                                    }}
                                >
                                    {t}
                                </p>
                            ))}
                        </div>
                        <div style={{ width: 1, background: BORDER }} />
                        <div style={{ flex: 1 }}>
                            <h3
                                style={{
                                    fontFamily: BODY,
                                    fontSize: 13,
                                    fontWeight: 700,
                                    color: BLACK,
                                    textTransform: "uppercase",
                                    letterSpacing: 0.5,
                                    margin: "0 0 12px",
                                    textDecoration: "underline",
                                    textUnderlineOffset: 4
                                }}
                            >
                                Opportunities
                            </h3>
                            {audit.keyFindings.opportunities.map((t, i) => (
                                <p
                                    key={i}
                                    style={{
                                        fontFamily: BODY,
                                        fontSize: 13.5,
                                        color: GRAY,
                                        lineHeight: 1.65,
                                        margin: "0 0 10px"
                                    }}
                                >
                                    {t}
                                </p>
                            ))}
                        </div>
                    </div>
                </Card>

                {/* Anonymous users see a partial preview (overview + key
                    findings above); the detailed breakdowns below are emailed
                    to them as the full PDF report. */}
                {!isLoggedIn && (
                    <Card>
                        <h2
                            style={{
                                fontFamily: DISPLAY,
                                fontSize: 22,
                                fontWeight: 600,
                                color: BLACK,
                                margin: "0 0 12px"
                            }}
                        >
                            Your full report is on its way
                        </h2>
                        <p
                            style={{
                                fontFamily: BODY,
                                fontSize: 14,
                                color: GRAY,
                                lineHeight: 1.65,
                                margin: 0
                            }}
                        >
                            {emailStatus.kind === "error" ? (
                                <>
                                    We hit a snag emailing your full report.{" "}
                                    {emailStatus.message}
                                </>
                            ) : emailStatus.kind === "sent" ? (
                                <>
                                    The complete audit — detailed Human Conversion &amp; Agentic
                                    Commerce breakdowns, projected performance, and category lift —
                                    has been sent to{" "}
                                    <strong style={{ color: BLACK }}>{auditEmail}</strong>. Check
                                    your inbox (and spam folder).
                                </>
                            ) : (
                                <>
                                    The complete audit — detailed Human Conversion &amp; Agentic
                                    Commerce breakdowns, projected performance, and category lift —
                                    is being emailed to{" "}
                                    <strong style={{ color: BLACK }}>{auditEmail}</strong>…
                                </>
                            )}
                        </p>
                        {emailStatus.kind === "error" && (
                            <button
                                onClick={handleEmailReport}
                                style={{
                                    marginTop: 16,
                                    background: GOLD,
                                    color: WHITE,
                                    border: "none",
                                    padding: "12px 28px",
                                    fontFamily: DISPLAY,
                                    fontSize: 13,
                                    letterSpacing: 1.5,
                                    fontWeight: 500,
                                    cursor: "pointer",
                                    borderRadius: 4,
                                    textTransform: "uppercase"
                                }}
                            >
                                Try Again
                            </button>
                        )}
                    </Card>
                )}

                {/* ── Human Conversion Readiness ── */}
                {isLoggedIn && (
                <Card>
                    <h2
                        style={{
                            fontFamily: DISPLAY,
                            fontSize: 22,
                            fontWeight: 600,
                            color: BLACK,
                            margin: "0 0 6px"
                        }}
                    >
                        Human Conversion Readiness: {audit.scores.humanConversion}/100
                    </h2>
                    <div style={{ maxWidth: 380, margin: "20px auto 28px" }}>
                        <RadarChart data={humanRadar} size={360} />
                    </div>
                    <Table columns={["Section", "Score", "Strengths", "Gaps"]} rows={humanRows} />
                </Card>
                )}

                {/* ── Agentic Commerce Readiness ── */}
                {isLoggedIn && (
                <Card>
                    <h2
                        style={{
                            fontFamily: DISPLAY,
                            fontSize: 22,
                            fontWeight: 600,
                            color: BLACK,
                            margin: "0 0 6px"
                        }}
                    >
                        Agentic Commerce Readiness: {audit.scores.agenticCommerce}/100
                    </h2>
                    <div style={{ maxWidth: 360, margin: "20px auto 28px" }}>
                        <RadarChart data={agenticRadar} size={340} />
                    </div>
                    <Table columns={["Section", "Score", "Strengths", "Gaps"]} rows={agenticRows} />
                </Card>
                )}

                {/* ── Projected Performance ── */}
                {isLoggedIn && (
                <Card>
                    <h2
                        style={{
                            fontFamily: DISPLAY,
                            fontSize: 22,
                            fontWeight: 600,
                            color: BLACK,
                            margin: "0 0 4px"
                        }}
                    >
                        Projected Performance with Expert-Led Product Content
                    </h2>
                    <p
                        style={{
                            fontFamily: BODY,
                            fontSize: 12,
                            color: GRAY_LIGHT,
                            margin: "0 0 6px"
                        }}
                    >
                        Best-in-class PDPs: 90+ · 80+ is considered good
                    </p>
                    <p
                        style={{
                            fontFamily: BODY,
                            fontSize: 11,
                            color: GRAY_LIGHT,
                            fontStyle: "italic",
                            margin: "0 0 20px",
                            lineHeight: 1.5
                        }}
                    >
                        Lift applies whether expert content is newly produced or already exists on the page but isn't yet discoverable by AI agents — if your PDP already has expert-led video or content that isn't surfaced in structured data, projected lift represents the value of making it machine-readable.
                    </p>
                    <Table
                        columns={["Category", "Current", "With TDC content", "Projected lift"]}
                        rows={projectedRows}
                    />
                </Card>
                )}

                {/* ── Category Lift Projection ── */}
                {isLoggedIn && (
                <Card>
                    <h2
                        style={{
                            fontFamily: DISPLAY,
                            fontSize: 22,
                            fontWeight: 600,
                            color: BLACK,
                            margin: "0 0 4px"
                        }}
                    >
                        Category Lift Projection
                    </h2>
                    <p
                        style={{
                            fontFamily: BODY,
                            fontSize: 12,
                            color: GRAY_LIGHT,
                            margin: "0 0 6px"
                        }}
                    >
                        Best-in-class PDPs: 90+ · 80+ is considered good
                    </p>
                    <p
                        style={{
                            fontFamily: BODY,
                            fontSize: 11,
                            color: GRAY_LIGHT,
                            fontStyle: "italic",
                            margin: "0 0 20px",
                            lineHeight: 1.5
                        }}
                    >
                        Lift applies whether expert content is newly produced or already exists on the page but isn't yet discoverable by AI agents — if your PDP already has expert-led video or content that isn't surfaced in structured data, projected lift represents the value of making it machine-readable.
                    </p>
                    <div style={{ display: "flex", gap: 32, flexWrap: "wrap" }}>
                        <div style={{ flex: "1 1 300px", minWidth: 280 }}>
                            <h3
                                style={{
                                    fontFamily: DISPLAY,
                                    fontSize: 15,
                                    fontWeight: 600,
                                    color: BLACK,
                                    margin: "0 0 12px",
                                    letterSpacing: 0.3,
                                    textTransform: "uppercase"
                                }}
                            >
                                Human PDP Experience
                            </h3>
                            <Table
                                columns={["Section", "Cur", "Proj", "Driver"]}
                                rows={humanLiftRows}
                            />
                        </div>
                        <div style={{ flex: "1 1 300px", minWidth: 280 }}>
                            <h3
                                style={{
                                    fontFamily: DISPLAY,
                                    fontSize: 15,
                                    fontWeight: 600,
                                    color: BLACK,
                                    margin: "0 0 12px",
                                    letterSpacing: 0.3,
                                    textTransform: "uppercase"
                                }}
                            >
                                Agentic PDP Experience
                            </h3>
                            <Table
                                columns={["Section", "Cur", "Proj", "Driver"]}
                                rows={agenticLiftRows}
                            />
                        </div>
                    </div>
                </Card>
                )}

                {/* ── Actions ── */}
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 16,
                        marginTop: 8,
                        marginBottom: 24,
                        flexWrap: "wrap"
                    }}
                >
                    {/* Logged-in / internal users get the full report on screen
                        plus Download and Email actions. Anonymous users have the
                        full report auto-emailed (see the useEffect above), so no
                        buttons are shown for them here. */}
                    {isLoggedIn && (
                        <button
                            onClick={handleDownload}
                            disabled={downloading}
                            style={{
                                background: GOLD,
                                color: WHITE,
                                border: "none",
                                padding: "14px 32px",
                                fontFamily: DISPLAY,
                                fontSize: 14,
                                letterSpacing: 1.5,
                                fontWeight: 500,
                                cursor: downloading ? "wait" : "pointer",
                                borderRadius: 4,
                                opacity: downloading ? 0.7 : 1,
                                textTransform: "uppercase"
                            }}
                        >
                            {downloading ? "Preparing PDF…" : "Download PDF"}
                        </button>
                    )}
                    {isLoggedIn && showEmailAction && auditEmail && (
                        <button
                            onClick={handleEmailReport}
                            disabled={emailStatus.kind === "sending" || emailStatus.kind === "sent"}
                            style={{
                                background: emailStatus.kind === "sent" ? BORDER : "none",
                                color: emailStatus.kind === "sent" ? BLACK : GOLD,
                                border: emailStatus.kind === "sent" ? "none" : `1px solid ${GOLD}`,
                                padding: "14px 32px",
                                fontFamily: DISPLAY,
                                fontSize: 14,
                                letterSpacing: 1.5,
                                fontWeight: 500,
                                cursor:
                                    emailStatus.kind === "sending"
                                        ? "wait"
                                        : emailStatus.kind === "sent"
                                            ? "default"
                                            : "pointer",
                                borderRadius: 4,
                                opacity: emailStatus.kind === "sending" ? 0.7 : 1,
                                textTransform: "uppercase"
                            }}
                        >
                            {emailStatus.kind === "sending"
                                ? "Sending…"
                                : emailStatus.kind === "sent"
                                    ? "Email Sent ✓"
                                    : "Email Me the Report"}
                        </button>
                    )}
                    {onAuditAnother && (
                        <button
                            onClick={onAuditAnother}
                            style={{
                                background: "none",
                                color: BLACK,
                                border: `1px solid ${BLACK}`,
                                padding: "13px 28px",
                                fontFamily: DISPLAY,
                                fontSize: 12,
                                letterSpacing: 1.5,
                                fontWeight: 500,
                                cursor: "pointer",
                                borderRadius: 4,
                                textTransform: "uppercase"
                            }}
                        >
                            Audit Another Page
                        </button>
                    )}
                    {typeof remainingToday === "number" && (
                        <span
                            style={{
                                fontFamily: BODY,
                                fontSize: 12,
                                color: GRAY_LIGHT,
                                marginLeft: "auto"
                            }}
                        >
                            {remainingToday} of 3 audits remaining today.
                        </span>
                    )}
                </div>

                {isLoggedIn && emailStatus.kind === "sent" && (
                    <div
                        style={{
                            fontFamily: BODY,
                            fontSize: 13,
                            color: GRAY,
                            marginTop: -8,
                            marginBottom: 24
                        }}
                    >
                        Sent to <strong style={{ color: BLACK }}>{auditEmail}</strong>. Check your inbox (and spam folder).
                    </div>
                )}
                {isLoggedIn && emailStatus.kind === "error" && (
                    <div
                        style={{
                            fontFamily: BODY,
                            fontSize: 13,
                            color: "var(--tool-down, #b00020)",
                            marginTop: -8,
                            marginBottom: 24
                        }}
                    >
                        {emailStatus.message}
                    </div>
                )}

                {/* ── CTA Banner ── */}
                {isLoggedIn ? (
                    <div
                        style={{
                            background: GOLD,
                            borderRadius: 8,
                            padding: "28px 36px"
                        }}
                    >
                        <h3
                            style={{
                                fontFamily: DISPLAY,
                                fontSize: 22,
                                fontWeight: 600,
                                color: WHITE,
                                margin: "0 0 8px"
                            }}
                        >
                            Get started today
                        </h3>
                        <p
                            style={{
                                fontFamily: BODY,
                                fontSize: 14,
                                color: WHITE,
                                lineHeight: 1.6,
                                margin: 0,
                                opacity: 0.92
                            }}
                        >
                            Content built for every shopper moment with the world's largest network of trusted experts. In
                            as little as 4–6 weeks.{" "}
                            Email{" "}
                            <a
                                href="mailto:sales@thedesirecompany.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    color: WHITE,
                                    textDecoration: "underline"
                                }}
                            >
                                sales@thedesirecompany.com
                            </a>{" "}
                            for questions, or request a quote.
                        </p>
                        <div style={{ marginTop: 18 }}>
                            <a
                                href={strategyLinkHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    fontFamily: BODY,
                                    display: "inline-block",
                                    background: CARD,
                                    color: BLACK,
                                    fontSize: 14,
                                    fontWeight: 700,
                                    borderRadius: 8,
                                    padding: "11px 20px",
                                    textDecoration: "none"
                                }}
                            >
                                {proposalCtaLabel} →
                            </a>
                            <div
                                style={{
                                    fontFamily: BODY,
                                    fontSize: 12,
                                    color: WHITE,
                                    opacity: 0.85,
                                    marginTop: 8
                                }}
                            >
                                Build a custom video strategy for this product. There is no obligation at this stage.
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        <FunnelChoiceCard
                            testId="funnel-choice-bottom"
                            registerHref={registerHref}
                            strategyHref={strategyLinkHref}
                        />
                        <p
                            style={{
                                fontFamily: BODY,
                                fontSize: 13,
                                color: GRAY,
                                textAlign: "center",
                                margin: "4px 0 0"
                            }}
                        >
                            Questions? Email{" "}
                            <a href="mailto:sales@thedesirecompany.com" style={{ color: GOLD, fontWeight: 600 }}>
                                sales@thedesirecompany.com
                            </a>
                        </p>
                    </>
                )}
            </div>
        </div>
    );
}
